import {useEffect, useRef, useState} from "react";
import type {AppState} from "../../../../../../data/state/AppState";
import {DomPortal} from "../../../../abstract/portal/DomPortal";
import {IconButton} from "../../../../../widgets/button/IconButton";
import {DomUtils, VerticalAlignment, type TransformObj, HorizontalAlignment} from "../../../../../../utils/dom/DomUtils";
import type {ICaptionSettings} from "../../../../../../data/models/ViewUtils";
import {XyiconFeature} from "../../../../../../generated/api/base";
import {ReactUtils} from "../../../../../utils/ReactUtils";
import {FloatingCaptionConfigurator} from "./FloatingCaptionConfigurator";

interface IIndividualCaptionStyleProps {
	readonly appState: AppState;
	readonly captionLabel: string;
	readonly fieldRefId: string;
	readonly captionSettings: ICaptionSettings;
}

export const IndividualCaptionStyle = (props: IIndividualCaptionStyleProps) => {
	const parentRef = useRef<HTMLDivElement>(null);
	const floatingRef = useRef<HTMLDivElement>(null);
	const [isConfiguratorOpen, setIsConfiguratorOpen] = useState<boolean>(false);
	const [configuratorTransform, setConfiguratorTransform] = useState<TransformObj>(null);

	const onCloseClick = () => {
		setIsConfiguratorOpen(false);
	};

	useEffect(() => {
		const updateTransform = () => {
			if (isConfiguratorOpen && parentRef?.current && floatingRef?.current) {
				const currentTransformStringified = JSON.stringify(configuratorTransform);
				const newTransform = DomUtils.getFixedFloatingElementPosition(
					parentRef.current,
					floatingRef.current,
					VerticalAlignment.topOuter,
					HorizontalAlignment.right,
				);
				const newTransformStringified = JSON.stringify(newTransform);

				if (currentTransformStringified !== newTransformStringified) {
					setConfiguratorTransform(newTransform);
				}
			}
		};

		updateTransform();

		const intervalId = window.setInterval(updateTransform, 0);

		return () => {
			window.clearInterval(intervalId);
		};
	}, [isConfiguratorOpen, configuratorTransform]);

	const onResetClick = () => {
		delete props.captionSettings.individualCaptionStyles[props.fieldRefId];
		props.appState.app.graphicalTools.spaceViewRenderer.actions.updateSpaceEditorCaptions(XyiconFeature.Boundary);
	};

	const individualCaptionStyle = props.captionSettings.individualCaptionStyles[props.fieldRefId];

	return (
		<>
			<div
				className={ReactUtils.cls("CaptionStyleButtonContainer hbox", {active: !!individualCaptionStyle})}
				ref={parentRef}
			>
				<IconButton
					icon="reset"
					title="Clear individual caption style"
					onClick={onResetClick}
					className={ReactUtils.cls("resetBtn", {active: !!individualCaptionStyle})}
				/>
				<IconButton
					icon="brush"
					title="Set individual caption style"
					onClick={() => setIsConfiguratorOpen(!isConfiguratorOpen)}
					className={ReactUtils.cls({active: !!individualCaptionStyle})}
				/>
			</div>
			{isConfiguratorOpen && (
				<DomPortal destination={props.appState.app.modalContainer}>
					<FloatingCaptionConfigurator
						divRef={floatingRef}
						transform={configuratorTransform}
						captionLabel={props.captionLabel}
						fieldRefId={props.fieldRefId}
						onCloseClick={onCloseClick}
						captionSettings={props.captionSettings}
						spaceViewRenderer={props.appState.app.graphicalTools.spaceViewRenderer}
					/>
				</DomPortal>
			)}
		</>
	);
};
